export type DateFilters = {
  filter_from_date: string
  filter_to_date: string
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const formatDate = (date: Date) => date.toISOString().split('T')[0]

const computeLastNDaysRange = (days: number = 30) => {
  const today = new Date()
  const prior = new Date()
  // Align with existing usage: last 30 days including today -> today - 30
  prior.setDate(today.getDate() - days)
  return { from: formatDate(prior), to: formatDate(today) }
}

/**
 * Resolve date filters from URL query (fromdate/todate) or default to the last N days.
 * - Reads `fromdate` and `todate` from the query string if provided and valid (yyyy-mm-dd).
 * - Falls back to `existing` values if provided and valid.
 * - Otherwise, defaults to the last `days` days including today (default 30).
 */
export function resolveDateFilters(params?: {
  search?: string | URLSearchParams
  existing?: Partial<DateFilters>
  days?: number
}): DateFilters {
  const { search, existing, days = 30 } = params || {}

  let query: URLSearchParams
  if (search instanceof URLSearchParams) {
    query = search
  } else if (typeof search === 'string') {
    query = new URLSearchParams(search)
  } else if (typeof window !== 'undefined' && window.location?.search) {
    query = new URLSearchParams(window.location.search)
  } else {
    query = new URLSearchParams()
  }

  const fromdate = query.get('fromdate') || undefined
  const todate = query.get('todate') || undefined

  const range = computeLastNDaysRange(days)

  const from = DATE_RE.test(fromdate || '')
    ? fromdate
    : DATE_RE.test(existing?.filter_from_date || '')
      ? (existing!.filter_from_date as string)
      : range.from
  const to = DATE_RE.test(todate || '')
    ? todate
    : DATE_RE.test(existing?.filter_to_date || '')
      ? (existing!.filter_to_date as string)
      : range.to

  return { filter_from_date: from!, filter_to_date: to! }
}

/** Convenience alias for 30-day default window */
export function getDateFilters(
  search?: string | URLSearchParams,
  existing?: Partial<DateFilters>,
): DateFilters {
  return resolveDateFilters({ search, existing, days: 30 })
}
